import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  Grid,
  Button,
  Typography,
  TextField,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Tooltip
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import { ListTags, Layout, CloseIcon, SelectInput } from 'components';
import { useSelector } from 'react-redux';
import { getPrintTags } from 'store/printTemplate/actions';
import Skeleton from 'react-loading-skeleton';
import InfoIcon from '@material-ui/icons/Info';
import { getEventTypeOptions } from 'helpers/templateHelper';

const useStyles = makeStyles(theme => ({
  root: {},
  cardHeader: {},
  radioLabel: {
    marginLeft: '0.7rem',
    alignItems: 'baseline'
  },
  radioGroup: {
    marginLeft: '1rem'
  },
  cardActions: {
    flexDirection: 'row-reverse'
  },
  inputGrid: {
    maxWidth: '40rem'
  },
  heightGrid: {
    maxWidth: '10rem'
  },
  checkedIcon: {
    color: theme.palette.primary
  },
  error: {
    color: theme.palette.error.main
  },
  headerContainer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between'
  }
}));

const Details = props => {
  const { className, staticContext, template, next, showNav, ...rest } = props;
  const {
    isLoading,
    goNext,
    thisTemplate,
    handleAutomationChange,
    supportedEventTypeOptions,
    supportedPrintTypeOptions,
    validationErrors,
    isTemplateReadOnly,
    showWarningMessage,
    isDefaultTemplate,
    showDefaultCheckbox,
    toggleDefaultTemplate,
    isTenantDefaultTemplate,
    handleChange,
    onFocus,
    onBlur,
    getErrorMessage,
    shouldShowError,
    updateCurrentErrorValidationSegment,
    modifyTags,
    modifyRows,
    modifyColumns,
    closeTemplate
  } = template;

  const templateOptions = [
    {
      label: 'PDF File',
      value: 'PDF'
    }
  ];

  const [clipboardInfo, setClipboardInfo] = useState({
    open: false,
    msg: 'Copy to Clipboard'
  });

  const SkeletonCardContent = () => (
    <CardContent>
      <Grid container spacing={3}>
        <Grid item md={6} xs={12}>
          <Typography
            display="block"
            style={{ fontSize: '2.2rem' }}
            className={classes.inputGrid}>
            <Skeleton />
          </Typography>
        </Grid>
      </Grid>
    </CardContent>
  );

  const tagList = useSelector(store => store.printTemplate.tagList);

  useEffect(() => {
    updateCurrentErrorValidationSegment('details');

    let options = [];

    options = supportedPrintTypeOptions?.filter(
      option => option.type === 'EVENT_TYPE_CATEGORY'
    );

    if (
      options?.length === 1 &&
      typeof thisTemplate.schemaIdentifier === 'undefined'
    ) {
      handleAutomationChange({
        target: {
          name: 'schemaIdentifier',
          value: options[0]?.schemaIdentifier
        }
      });
    }
  }, [thisTemplate, updateCurrentErrorValidationSegment]);

  const classes = useStyles();

  const level1 = makeStyles(theme => ({
    root: {
      paddingLeft: theme.spacing(1),
      pointerEvents: 'none',
      backgroundColor: '#A5A5A5',
      '&:hover': {
        backgroundColor: '#A5A5A5'
      }
    }
  }))();

  const level2 = makeStyles(theme => ({
    root: {
      paddingLeft: theme.spacing(3),
      pointerEvents: 'none',
      backgroundColor: '#CECECE',
      '&:hover': {
        backgroundColor: '#CECECE'
      }
    }
  }))();

  const level3 = makeStyles(theme => ({
    root: {
      paddingLeft: theme.spacing(5),
      pointerEvents: 'none',
      backgroundColor: '#E5E5E5',
      '&:hover': {
        backgroundColor: '#E5E5E5'
      }
    }
  }))();

  const level4 = makeStyles(theme => ({
    root: {
      paddingLeft: theme.spacing(7),
      backgroundColor: '#FFFFFF'
    }
  }))();

  const iconStyle = makeStyles(() => ({
    root: {
      minWidth: '30px'
    }
  }))();

  const areInitialSettingsDisabled = () =>
    isTemplateSaved() ||
    !!thisTemplate.jsonContent ||
    thisTemplate.content?.length > 0;

  const isTemplateSaved = () => thisTemplate.id && thisTemplate.id.length > 0;

  const copyToClipboard = async text => {
    await navigator.clipboard.writeText(text);
    openTooltip('Copied to Clipboard');
  };

  const handleTooltipClose = () => {
    setClipboardInfo({ ...clipboardInfo, open: false });
  };

  const handleTooltipOpen = () => {
    setClipboardInfo({ open: true, msg: 'Copy to Clipboard' });
  };

  const openTooltip = msg => {
    setClipboardInfo({ open: true, msg });
  };

  const templateTypes = {
    list: templateOptions,
    keyId: 'value',
    valueId: 'value',
    displayValueId: 'label'
  };

  return (
    <div className={classes.root}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <form autoComplete="off">
          <React.Fragment>
            {showWarningMessage() && (
              <Alert severity="warning">
                Changes made to Global Default template will be reflected in all
                tenants and properties.
              </Alert>
            )}
            <div className={classes.headerContainer}>
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <CardHeader
                  subheader="* indicates a required field"
                  title="Template Details"
                  titleTypographyProps={{ variant: 'h1' }}
                />
                {isTemplateReadOnly() && (
                  <Tooltip
                    title={
                      'You do not have access to edit this template. ' +
                      (isTenantDefaultTemplate()
                        ? "Tenant Default templates can be edited only with 'ALL' property."
                        : 'Only admin can edit Global Default templates.')
                    }>
                    <InfoIcon size="small" style={{ marginTop: '26px' }} />
                  </Tooltip>
                )}
              </div>
              <CloseIcon onClickClose={closeTemplate} />
            </div>
          </React.Fragment>
          <Divider />
          {isLoading && <SkeletonCardContent />}
          {!isLoading && (
            <CardContent>
              <Grid container spacing={3}>
                <Grid item md={6} xs={12}>
                  {thisTemplate.id && (
                    <Grid item xs={12} className={classes.inputGrid}>
                      <TextField
                        fullWidth
                        label="Template Id"
                        margin="dense"
                        name="id"
                        value={thisTemplate.id || ''}
                        variant="outlined"
                        onFocus={onFocus}
                        onBlur={onBlur}
                        disabled={true}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip
                                title={clipboardInfo.msg}
                                open={clipboardInfo.open}
                                onClose={handleTooltipClose}
                                leaveDelay={500}
                                interactive={true}>
                                <FileCopyOutlinedIcon
                                  style={{ cursor: 'pointer', color: '#777' }}
                                  onMouseEnter={handleTooltipOpen}
                                  onClick={() =>
                                    copyToClipboard(thisTemplate.id)
                                  }
                                />
                              </Tooltip>
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} className={classes.inputGrid}>
                    <TextField
                      fullWidth
                      label="Template Name"
                      margin="dense"
                      name="name"
                      onChange={handleChange}
                      required
                      value={thisTemplate.name || ''}
                      variant="outlined"
                      onFocus={onFocus}
                      onBlur={onBlur}
                      disabled={isTemplateReadOnly()}
                      error={shouldShowError('name')}
                      helperText={getErrorMessage('name')}
                    />
                  </Grid>
                  <Grid item xs={12} className={classes.inputGrid}>
                    <TextField
                      fullWidth
                      label="Template Description"
                      margin="dense"
                      name="templateDescription"
                      onChange={handleChange}
                      onFocus={onFocus}
                      onBlur={onBlur}
                      disabled={isTemplateReadOnly()}
                      placeholder="Description"
                      required
                      value={thisTemplate.templateDescription || ''}
                      variant="outlined"
                      error={shouldShowError('templateDescription')}
                      helperText={getErrorMessage('templateDescription')}
                    />
                  </Grid>

                  <Grid item xs={12} className={classes.inputGrid}>
                    <SelectInput
                      fullWidth
                      label="Event Type Category"
                      margin="dense"
                      name="schemaIdentifier"
                      onFocus={onFocus}
                      onBlur={onBlur}
                      disabled={
                        areInitialSettingsDisabled() || isTemplateReadOnly()
                      }
                      onChange={handleAutomationChange}
                      required
                      value={thisTemplate.schemaIdentifier || ''}
                      selectProps={{
                        renderValue: option => option
                      }}
                      error={shouldShowError('schemaIdentifier')}
                      helperText={getErrorMessage('schemaIdentifier')}
                      options={getEventTypeOptions(supportedPrintTypeOptions)}
                    />
                  </Grid>

                  <Grid item xs={12} className={classes.inputGrid}>
                    <SelectInput
                      fullWidth
                      label="Print Template Type"
                      margin="dense"
                      name="type"
                      onFocus={onFocus}
                      onBlur={onBlur}
                      disabled={
                        areInitialSettingsDisabled() || isTemplateReadOnly()
                      }
                      onChange={handleChange}
                      required
                      value={thisTemplate.type || ''}
                      error={shouldShowError('type')}
                      helperText={getErrorMessage('type')}
                      options={templateTypes}
                    />
                  </Grid>
                  {
                    <Grid item xs={12} className={classes.inputGrid}>
                      <Layout
                        thisTemplate={thisTemplate}
                        modifyRows={modifyRows}
                        modifyColumns={modifyColumns}
                        isTemplateReadOnly={isTemplateReadOnly()}></Layout>
                    </Grid>
                  }
                  {!isLoading && (
                    <Grid item xs={12} className={classes.inputGrid}>
                      <ListTags
                        tags={thisTemplate.tags || []}
                        action={getPrintTags}
                        modifyTags={modifyTags}
                        allTags={tagList}
                        isTemplateReadOnly={isTemplateReadOnly()}></ListTags>
                    </Grid>
                  )}
                  {showDefaultCheckbox() && (
                    <Grid
                      item
                      xs={12}
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        marginLeft: '0px'
                      }}>
                      <FormControlLabel
                        disabled={isDefaultTemplate()}
                        style={{ marginLeft: '0px' }}
                        label="Default Template"
                        control={
                          <Checkbox
                            checked={thisTemplate.defaultTemplate || false}
                            onChange={toggleDefaultTemplate}
                            color="primary"
                            value="true"
                          />
                        }
                      />
                      {!isDefaultTemplate() && (
                        <Tooltip title="If this checkbox is enabled, this template will be available for all tenants and properties.">
                          <InfoIcon size="small" style={{ marginTop: '8px' }} />
                        </Tooltip>
                      )}
                    </Grid>
                  )}
                </Grid>
                <Grid item md={6} xs={12}>
                  <Grid item xs={12} className={classes.inputGrid}>
                    <TextField
                      fullWidth
                      label="Header (HTML)"
                      margin="dense"
                      name="header"
                      onChange={handleChange}
                      multiline
                      rows="4"
                      value={thisTemplate.header || ''}
                      variant="outlined"
                      onFocus={onFocus}
                      onBlur={onBlur}
                      disabled={isTemplateReadOnly()}
                      error={shouldShowError('header')}
                      helperText={getErrorMessage('header')}
                    />
                  </Grid>
                  <Grid item xs={12} className={classes.heightGrid}>
                    <TextField
                      fullWidth
                      label="Header Height (in px)"
                      margin="dense"
                      name="headerHeight"
                      onChange={handleChange}
                      type='number'
                      // Add these props to prevent special characters
                      onKeyDown={(e) => {
                        // Block e, +, -, . characters
                        if (["e", "E", "+", "-", "."].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      // Set min to 0 to prevent negative values
                      inputProps={{ min: 0 }}
                      value={thisTemplate.headerHeight}
                      size='small'
                      variant="outlined"
                      onFocus={onFocus}
                      onBlur={onBlur}
                      disabled={isTemplateReadOnly()}
                      error={shouldShowError('headerHeight')}
                      helperText={getErrorMessage('headerHeight')}
                    />
                  </Grid>
                  <Grid item xs={12} className={classes.inputGrid}>
                    <TextField
                      fullWidth
                      label="Footer (HTML)"
                      margin="dense"
                      name="footer"
                      onChange={handleChange}
                      multiline
                      rows="4"
                      value={thisTemplate.footer || ''}
                      variant="outlined"
                      onFocus={onFocus}
                      onBlur={onBlur}
                      disabled={isTemplateReadOnly()}
                      error={shouldShowError('footer')}
                      helperText={getErrorMessage('footer')}
                    />
                  </Grid>
                  <Grid item xs={12} className={classes.heightGrid}>
                    <TextField
                      fullWidth
                      label="Footer Height (in px)"
                      margin="dense"
                      name="footerHeight"
                      onChange={handleChange}
                      type='number'
                      // Add these props to prevent special characters
                      onKeyDown={(e) => {
                        // Block e, +, -, . characters
                        if (["e", "E", "+", "-", "."].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      // Set min to 0 to prevent negative values
                      inputProps={{ min: 0 }}
                      value={thisTemplate.footerHeight}
                      size='small'
                      variant="outlined"
                      onFocus={onFocus}
                      onBlur={onBlur}
                      disabled={isTemplateReadOnly()}
                      error={shouldShowError('footerHeight')}
                      helperText={getErrorMessage('footerHeight')}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          )}
          {(next || showNav) && (
            <>
              <Divider />
              <CardActions className={classes.cardActions}>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={isLoading}
                  link={next}
                  onClick={goNext}>
                  Next
                </Button>
              </CardActions>
            </>
          )}
        </form>
      </Card>
    </div>
  );
};

export default Details;
